import * as Yup from 'yup';
import React, { useState } from 'react';
import { Flex, TextV2, ButtonV2, FormControl, Box, BoxProps } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useFormik } from 'formik';
import { Edit, Search } from '@withjoy/joykit/icons';
import { animationTransition } from '@shared/joykit/packages/core/utils';
import { Container, Placeholder } from '@withjoy/joykit/components/SelectV1/styles';
import { LocationInput } from '@shared/components/LocationInput';
import { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { StyledInputContainer } from '@withjoy/joykit/components/InputV1/StyledInputContainer';
import { Override } from '@shared/utils/overrides';
import { CSSObject } from '@withjoy/joykit/components/Box/Box.types';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { pxToRem } from '@withjoy/joykit/theme';

export enum PlacePredictionType {
  All = 'All',
  PointsOfInterest = 'PointsOfInterest',
}

const PlacePredictionTypeValues: Record<PlacePredictionType, string[]> = {
  [PlacePredictionType.All]: [],
  [PlacePredictionType.PointsOfInterest]: ['lodging', 'point_of_interest', 'locality'],
};

export type HotelsSearchInputOverrides = Readonly<{
  Root?: Override<BoxProps>;
  Label?: Override<CSSObject>;
  Input?: Override<CSSObject>;
  Button?: Override<CSSObject>;
}>;
export interface HotelsSearchInputFormProps {
  location: Maybe<Location>;
}
export interface HotelsSearchInputProps {
  eventLocationData?: Location;
  locationComponentRestrictions?: google.maps.places.ComponentRestrictions;
  onFocus?: () => void;
  handleOnSearch: (values: HotelsSearchInputFormProps) => void;
  handleOnChangeLocation: (values: HotelsSearchInputFormProps) => void;
  overrides?: HotelsSearchInputOverrides;
  label?: string;
  cta?: string;
  numberOfRoomBlocks?: number;
  showRoomBlocks?: boolean;
  skipFormValidation?: boolean;
  placePredictionType?: PlacePredictionType;
  hideLabel?: boolean;
  hideIcon?: boolean;
}

export const HotelsSearchInput: React.FC<HotelsSearchInputProps> = props => {
  const {
    onFocus,
    handleOnSearch,
    handleOnChangeLocation,
    eventLocationData,
    locationComponentRestrictions,
    overrides,
    label,
    cta,
    showRoomBlocks,
    numberOfRoomBlocks,
    skipFormValidation,
    placePredictionType = PlacePredictionType.All,
    hideLabel,
    hideIcon = false,
  } = props;
  const { t } = useTranslation('hotelBlock');
  const { list } = t('hotelSelection');
  const { location, locationPlaceholder, roomBlocks, near } = list.search;
  const placePredictionTypes = PlacePredictionTypeValues[placePredictionType];

  const [isEditing, setIsEditing] = useState(!showRoomBlocks);

  const formik = useFormik<HotelsSearchInputFormProps>({
    initialValues: {
      location: eventLocationData,
    },
    validationSchema: Yup.object<HotelsSearchInputFormProps>({
      location: Yup.object<Location>({ place: Yup.string().required(), lng: Yup.number(), lat: Yup.number(), placeId: Yup.string() }),
    }),
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: values => {
      if (isEditing) {
        if (values.location?.place !== eventLocationData?.place || skipFormValidation) {
          handleOnSearch(values);
        }
        if (!skipFormValidation && showRoomBlocks) {
          setIsEditing(false);
        }
      } else {
        setIsEditing(true);
      }
    },
  });

  return (
    <Flex
      as={'form'}
      onSubmit={formik.handleSubmit}
      justifyContent="center"
      columnGap={16}
      borderRadius={32}
      boxShadow="0px 4px 16px 0px rgba(0, 0, 0, 0.07), 0px 7px 27px 0px rgba(44, 41, 37, 0.06)"
      paddingY={3}
      paddingLeft={6}
      paddingRight={3}
      width={'100%'}
      maxWidth={448}
      height={64}
      onFocus={() => onFocus?.()}
      border={'1px solid'}
      borderColor={'mono5'}
      _hover={{
        borderColor: '#5f269b',
      }}
      _focusWithin={{
        boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.07), 0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 0 0px 1px #5f269b',
      }}
      transition={animationTransition('border-color', 'box-shadow')}
      zIndex={'banner'}
      {...overrides?.Root?.props}
    >
      <FormControl
        overrides={{
          Label: {
            props: {
              color: 'mono10',
              marginBottom: '2px',
            },
          },
          Root: {
            props: {
              justifyContent: 'center',
            },
          },
        }}
        display={'flex'}
        flexDirection={'column'}
        verticalAlign={'bottom'}
        label={
          hideLabel ? (
            <></>
          ) : (
            <TextV2
              typographyVariant={'label3'}
              __css={overrides?.Label?.props}
            >
              {label ?? location()}
            </TextV2>
          )
        }
        flexGrow={1}
        __css={{
          [`${StyledInputContainer}`]: {
            ['input']: {
              minHeight: 'auto',
              height: 'auto',
              paddingLeft: '0!important',
              fontSize: '1rem',
              ...(!hideIcon ? { width: 'calc(100% - 51px)!important' } : {}),
              ...overrides?.Input?.props,
            },
          },
          [`${Placeholder}`]: {
            left: '0!important',
          },
          [`${Container}`]: {
            ...(!hideIcon ? { width: 'calc(100% + 51px)!important' } : {}),
          },
        }}
      >
        {!isEditing ? (
          <Flex
            gap={1}
            height={pxToRem(24)}
            alignItems="center"
          >
            <TextV2
              typographyVariant="button2"
              fontWeight={600}
              color="#4951EF"
              flex="none"
            >
              {roomBlocks({ roomBlocks: numberOfRoomBlocks })}
            </TextV2>
            <TextV2
              typographyVariant="button2"
              fontWeight={400}
              color="mono8"
            >
              {near()}
            </TextV2>
            <TextEllipsisWrapper
              typographyVariant="button2"
              fontWeight={400}
            >
              {formik.values.location?.place}
            </TextEllipsisWrapper>
          </Flex>
        ) : (
          <LocationInput
            {...formik.getFieldProps('location')}
            onLocationChange={location => {
              formik.setFieldValue('location', location);
              handleOnChangeLocation({ location });
              if (showRoomBlocks) {
                setIsEditing(false);
              }
            }}
            includeCloseIcon={false}
            placeholder={`${locationPlaceholder()}`}
            includeMapIcon={false}
            placePredictionTypes={placePredictionTypes}
            allowCustomPlace={false}
            componentRestrictions={locationComponentRestrictions}
            defaultValue={eventLocationData?.place || ''}
            noBorder
          />
        )}
      </FormControl>
      {!hideIcon && (
        <Box flexShrink={1}>
          <ButtonV2
            variant="solid"
            intent="neutral"
            shape="rounded"
            startIcon={numberOfRoomBlocks && !isEditing ? <Edit /> : <Search />}
            type="submit"
            width={48}
            height={48}
            overrides={{ Root: overrides?.Button }}
          >
            {cta}
          </ButtonV2>
        </Box>
      )}
    </Flex>
  );
};
